import { IMAGE_SIZE_CODE } from '../../../constants/image-size-codes';

/**
 * @enum COMMAND_MODES
 * @type {{MANUAL_MATCH: string, NUM_REC: string, FLUSH: string, MATCH: string}}
 */
export const COMMAND_MODES = {
  MATCH: 'match',
  FLUSH: 'flush',
  NUM_REC: 'numrec',
  MANUAL_MATCH: 'manual_match',
};

/**
 * Maximum number of votes a single matching lease should fetch.
 *
 * @type {number}
 */
export const MAYBE_QUERY_COUNT = 20;

/**
 * Artificial delay to wait before fetching new votes.
 *
 * @type {number}
 */
export const MAYBE_VOTE_ARTIFICIAL_REFETCH_DELAY_MS = 500;

/**
 * Time window between voting and being able to undo.
 * A value of 0 disables undo globally.
 *
 * @type {number}
 */
export const VOTE_UNDO_WINDOW_MS = 20000;

/**
 * How much time per vote should the matched votes be leased for.
 * This counts on *fetched* votes, so if the query returns
 * 20 votes and the time is 15s the entire batch (each vote in it)
 * will be leased for 20 * 15 = 300 seconds.
 *
 * @type {number}
 */
export const MAYBE_TIME_PER_VOTE_SEC = Math.ceil(
  15 + VOTE_UNDO_WINDOW_MS / 1000,
);

/**
 * Maximum number of votes a single flushing lease should fetch.
 *
 * @type {number}
 */
export const FLUSH_QUERY_COUNT = 9999;

/**
 * How much time per vote should the flushable votes be leased for.
 * This counts on *fetched* votes, so if the query returns
 * 20 votes and the time is 15s the entire batch (each vote in it)
 * will be leased for 20 * 15 = 300 seconds.
 *
 * @type {number}
 */
export const FLUSH_TIME_PER_VOTE_SEC = 35;

/**
 * Artificial delay to wait before fetching new votes.
 *
 * @type {number}
 */
export const FLUSH_VOTE_ARTIFICIAL_REFETCH_DELAY_MS = 1250;

/**
 * Artificially disable voting buttons after every call to avoid
 * accidental clicks due to momentum.
 *
 * @type {number}
 */
export const VOTE_DISABLE_PERIOD_MS = 750;

/**
 * Threshold where a lease should be extended.
 * E.g. If now + LEASE_EXTEND_THRESHOLD_SEC >= lease.expires_at
 * the lease should be extended.
 *
 * @type {number}
 */
export const LEASE_EXTEND_THRESHOLD_SEC = 30;

/**
 * @enum FLUSH_MODE_COLS
 * @type {{ONE: string, TWO: string, THREE: string}}
 */
export const FLUSH_MODE_COLS = {
  ONE: 'flush-mode-one-col',
  TWO: 'flush-mode-two-cols',
  THREE: 'flush-mode-three-cols',
};

export const COMMAND_DEFAULT_IMAGE_SIZE = IMAGE_SIZE_CODE.LARGE1X;
export const COMMAND_NAV_THUMB_IMAGE_SIZE = IMAGE_SIZE_CODE.LARGE;
export const COMMAND_ACTIVE_REF_IMAGE_LIMIT = 3;

/**
 * Threshold over or equal to which a vote (and its attached cluster, if any)
 * is immediately flushed after being turned from POSSIBLE (maybe) to POSITIVE
 * (totally) by matchers.
 *
 * Setting this to 1 disables it.
 *
 * @type {number}
 */
export const COMMAND_MATCH_FLUSH_THRESHOLD = 1;

/**
 * Different statuses for Pub Matching Votes depending on the user's action.
 *
 * @enum COMMAND_PUB_MATCHING_VOTE_STATUS
 * @type {{NO_ACTION: string, NUMBER_FOUND: string, NO_NUMBER_FOUND: string, NOT_SURE: string}}
 */
export const COMMAND_PUB_MATCHING_VOTE_STATUS = {
  NO_ACTION: 'NO_ACTION',
  NUMBER_FOUND: 'NUMBER_FOUND',
  NO_NUMBER_FOUND: 'NO_NUMBER_FOUND',
  NOT_SURE: 'NOT_SURE',
};

export const PUB_MATCHING_VOTE_LIMIT = 25;
export const PUB_MATCHING_TIME_PER_VOTE_SEC = 20;

/**
 * Threshold where a lease should be extended for PUB_MATCHING
 * E.g. If now + PUB_MATCHING_LEASE_EXTEND_THRESHOLD_SEC >= lease.expires_at
 * the lease should be extended.
 *
 * @type {number}
 */
export const PUB_MATCHING_LEASE_EXTEND_THRESHOLD_SEC = 180; // 3 min

/**
 * Threshold where a lease should be extended for NUM_REC
 * E.g. If now + NUM_REC_LEASE_EXTEND_THRESHOLD_SEC >= lease.expires_at
 * the lease should be extended.
 *
 * @type {number}
 */
export const NUM_REC_LEASE_EXTEND_THRESHOLD_SEC = 300; // 5 min.
export const NUM_REC_LEASE_TIME = 600; // 10 min.

/**
 * Threshold where a lease should be extended for MANUAL_MATCH
 * E.g. If now + MANUAL_MATCH_LEASE_EXTEND_THRESHOLD_SEC >= lease.expires_at
 * the lease should be extended.
 *
 * @type {number}
 */
export const MANUAL_MATCH_LEASE_EXTEND_THRESHOLD_SEC = 60; // 1min
export const MANUAL_MATCH_LEASE_TIME = 300; // 5 min.

/**
 * @enum NUM_REC_COLS
 * @type {{THREE: string, FOUR: string, SIX: string}}
 */
export const NUM_REC_COLS = {
  RELAXED: 'relaxed',
  COMPACT: 'compact',
};

export const MANUAL_MATCH_PHOTO_LIMIT = 11;

/**
 * @enum NUM_REC_COLOR_SORT
 * @type {{ ENABLED: string, DISABLED: string, INVERTED: string }}
 */
export const NUM_REC_COLOR_SORT = {
  ENABLED: 'ENABLED',
  DISABLED: 'DISABLED',
  INVERTED: 'INVERTED',
};

/**
 * @enum DETECTED_RACE_TYPE
 * @type {{AFRICAN_AMERICAN: string, ASIAN: string, CAUCASIAN: string}}
 */
export const DETECTED_RACE_TYPE = {
  AFRICAN_AMERICAN: 'AFRICAN_AMERICAN',
  ASIAN: 'ASIAN',
  CAUCASIAN: 'CAUCASIAN',
};

/**
 * @enum FACE_MATCH_VOTE_SOURCE_TOOL
 * @type {{MATCHING_TAG: string}}
 */
export const FACE_MATCH_VOTE_SOURCE_TOOL = {
  MATCHING_TAG: 'MATCHING_TAG',
};
